import { generateAccessControlToken, getSessionToken, setOriginScopedAccessControlToken } from '@netlify/app-auth'
import { Button, Card, Code, Heading, Link, Paragraph, TextInputField } from 'evergreen-ui'
import { useState } from 'react'

import { getUser } from '~/api/user'
import Flex from '~/components/Flex'
import { SESSION_STORAGE_KEY } from '~/helpers/auth'
import { getEnvironmentFromWindowLocation } from '~/utils/env'

export default function DevAccessToken() {
  const [isLoading, setLoading] = useState(false)
  const [token, setToken] = useState('')
  const [error, setError] = useState('')

  const env = getEnvironmentFromWindowLocation()
  const url = `https://app.${env === 'production' ? 'netlify' : 'netlifystg'}.com`

  const save = async () => {
    const isJson = v => /^{/.test(v.trim()) && /access_token/.test(v)
    const stringTokenObj = isJson(token) ? token : JSON.stringify({ access_token: token })
    const parsedTokenObj = JSON.parse(stringTokenObj)

    let tokenToUseForGeneration = parsedTokenObj.access_token

    // support the flow where auth requires exchanging a raw access_token with an opaque access
    // control token for persistent auth

    if (!parsedTokenObj.access_token || parsedTokenObj.access_token === 'use-http-auth-token') {
      if (parsedTokenObj.access_control_token) {
        tokenToUseForGeneration = parsedTokenObj.access_control_token
      } else {
        tokenToUseForGeneration = undefined
        // eslint-disable-next-line no-console
        console.log(
          'there is no suitable token to use for auth. User needs to copy an nf-auth cookie value to use',
          parsedTokenObj
        )
      }
    }

    if (tokenToUseForGeneration) {
      const shortLivedACToken = await generateAccessControlToken(tokenToUseForGeneration)
      await setOriginScopedAccessControlToken({
        originContext: 'cdp',
        accessControlToken: shortLivedACToken || '',
        storageKeyOverride: SESSION_STORAGE_KEY,
      })
      parsedTokenObj.access_control_token = getSessionToken({
        preferAccessControlToken: true,
        storageKeyOverride: SESSION_STORAGE_KEY,
      })
    }

    // test that token is valid by accessing getUser endpoint
    setLoading(true)
    getUser()
      .then(() => document.location.assign('/'))
      .catch(() => setError(`This value didn't work, are you sure you used the one from ${env}?`))
      .finally(() => setLoading(false))
  }

  return (
    <Flex alignItems="center" background="tint2" height="100vh" justifyContent="center" width="100%">
      <Card background="white" elevation={1} padding={40}>
        <Heading marginBottom={40} size={600} textAlign="center">
          Provide access token 🔑
        </Heading>
        <Paragraph>
          1️⃣ Go to{' '}
          <Link href={url} target="_blank">
            the main app
          </Link>{' '}
          with a logged in session and open up devtools.
        </Paragraph>
        <Paragraph marginY="default">
          2️⃣ Navigate to cookie storage. {`(In Chrome, Application => Cookies => app.netlify.com.) `} Copy the _nf-auth
          value.
        </Paragraph>
        <Paragraph>
          3️⃣ Come back here and <Code>⌘V</Code> in the text field below:
        </Paragraph>
        <TextInputField
          className="monospace"
          inputHeight={40}
          isInvalid={!!error}
          label="Paste your magical access token value here"
          marginTop={24}
          onChange={e => setToken(e.target.value)}
          validationMessage={!error ? undefined : error}
          value={token}
          width="100%"
        />
        <Button appearance="primary" disabled={!token} isLoading={isLoading} onClick={save} size="large">
          Save locally
        </Button>
      </Card>
    </Flex>
  )
}
